import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';

import {
  setTaxpayerAuthenticationConfig,
  updateTaxpayerAuthenticationField,
  resetTaxpayerAuthenticationConfig,
  setTaxpayerAuthenticationLoading,
  setTaxpayerAuthenticationSubmitting,
} from '../../store/slices/hacienda/taxpayerAuthenticationConfigSlice';

import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';

import TaxpayerAuthenticationConfigService from '../../services/hacienda/TaxpayerAuthenticationConfigService';
import { TaxpayerAuthenticationConfigDTO } from '../../types/hacienda/TaxpayerAuthenticationTypes';
import useModals from '../useModals';

export const useTaxpayerAuthenticationConfig = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    form,
    isLoading,
    isSubmitting,
    error
  } = useSelector((state: RootState) => state.taxpayerAuthenticationConfig);

  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();
  const { openModal } = useModals();


  const loadConfiguration = async () => {
    try {
      dispatch(setTaxpayerAuthenticationLoading(true));
      showLoadingIndicator();

      const response = await TaxpayerAuthenticationConfigService.getConfiguration();
      const { statusCode, data } = response;

      if (statusCode === 200) {
        dispatch(setTaxpayerAuthenticationConfig(data));
        return response;
      }

      return null;
    } catch (error: any) {
      handle(error);
      return null;
    } finally {
      dispatch(setTaxpayerAuthenticationLoading(false));
      hideLoadingIndicator();
    }
  };




  /**
   * ✅ Guardar o actualizar configuración
   */
  const saveConfiguration = async (form: TaxpayerAuthenticationConfigDTO) => {
    try {
      dispatch(setTaxpayerAuthenticationSubmitting(true));
      showLoadingIndicator();

      const isUpdate = !!form.id;
      const response = isUpdate
        ? await TaxpayerAuthenticationConfigService.updateConfiguration(form.id!, form)
        : await TaxpayerAuthenticationConfigService.createConfiguration(form);

      const { statusCode, data, type, title, message } = response;

      if (statusCode === 200 || statusCode === 201) {
        dispatch(setTaxpayerAuthenticationConfig(data));
        openModal(type, { title, message, });
      } else {
        showAlert('error', 'Error', 'No se pudo guardar la configuración.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setTaxpayerAuthenticationSubmitting(false));
      hideLoadingIndicator();
    }
  };

  /**
   * ✅ Actualizar un campo individual
   */
  const updateField = <K extends keyof TaxpayerAuthenticationConfigDTO>(
    field: K,
    value: TaxpayerAuthenticationConfigDTO[K]
  ) => {
    dispatch(updateTaxpayerAuthenticationField({ field, value }));
  };

  /**
   * ✅ Resetear formulario
   */
  const resetForm = () => {
    dispatch(resetTaxpayerAuthenticationConfig());
  };

  /**
  * 🔍 Obtener el NIT del sistema desde el backend y actualizar el formulario
  */
  const loadSystemTaxId = async () => {
    try {
      const response = await TaxpayerAuthenticationConfigService.getSystemTaxId();

      const { statusCode, data } = response;
      if (statusCode === 200) {
        updateField('taxId', data);
      }
    } catch (error: any) {
      handle(error);
    }
  };

  return {
    form,
    isLoading,
    isSubmitting,
    error,
    updateField,
    resetForm,
    loadConfiguration,
    saveConfiguration,
    loadSystemTaxId
  };
};
